import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { 
  BookOpen, 
  HandHeart, 
  Target,
  List,
  ChartLineUp
} from '@phosphor-icons/react';
import { 
  TasbihIcon, 
  Quran01Icon, 
  RamadhanMonthIcon, 
  DuaIcon, 
  SmartPhone02Icon, 
  DownloadSquare01Icon 
} from "hugeicons-react";

const DeenHelpers = () => {
  const { isDarkMode } = useDarkMode();
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect if the user is on desktop
  useEffect(() => {
    const checkIfDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024); // 1024px is typical breakpoint for desktop
    };
    
    // Check on initial load
    checkIfDesktop();
    
    // Check on resize
    window.addEventListener('resize', checkIfDesktop);
    
    return () => window.removeEventListener('resize', checkIfDesktop);
  }, []);

  const helpers = [
    {
      title: 'Quran Reader',
      description: 'Read, reflect, and track your daily Quran reading progress',
      icon: Quran01Icon,
      link: '/quran',
      color: 'emerald'
    },
    {
      title: 'Digital Tasbih',
      description: 'Keep track of your dhikr with an elegant digital counter',
      icon: TasbihIcon,
      link: '/dhikr-helper',
      color: 'emerald'
    },
    {
      title: 'Goal Planner',
      description: 'Set and track your spiritual goals',
      icon: RamadhanMonthIcon,
      link: '/mobile-goals',
      color: 'emerald'
    },
    {
      title: 'Dua Explorer',
      description: 'Browse authentic duas from the Quran and Sunnah',
      icon: DuaIcon,
      link: '/dua-explorer',
      color: 'emerald'
    }
  ];

  // Desktop download prompt component
  const DesktopAppPrompt = () => (
    <div className={`rounded-2xl p-8 transition-all duration-300 max-w-3xl mx-auto ${
      isDarkMode 
        ? 'bg-emerald-950/40 backdrop-blur-xl border border-emerald-900/50' 
        : 'bg-emerald-50/90 backdrop-blur-md shadow-sm border border-emerald-100'
    }`}>
      <div className="flex items-start gap-6">
        <div className={`flex-shrink-0 w-16 h-16 rounded-xl flex items-center justify-center ${
          isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'
        }`}>
          <SmartPhone02Icon size={32} className={`${
            isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
          }`} />
        </div>
        
        <div className="flex-1">
          <h3 className={`text-xl font-medium mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Enhanced Experience on Mobile
          </h3>
          <p className={`text-md mb-6 leading-relaxed ${
            isDarkMode ? 'text-white/70' : 'text-gray-600'
          }`}>
            These Deen Helper features are accessible on mobile browsers too, but are best experienced on our iOS app. Download from the App Store for the optimal experience. Desktop and Android versions coming soon.
          </p>
          
          <a 
            href="https://apps.apple.com/us/app/ummahti"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
              isDarkMode 
                ? 'bg-emerald-600 hover:bg-emerald-500 text-black' 
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
          >
            <DownloadSquare01Icon size={20} className="current-color" />
            Download on App Store
          </a>
        </div>
      </div>
    </div>
  );

  // Mobile version of the page - with background and cards
  const MobileView = () => (
    <div className={`min-h-screen relative overflow-hidden ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-100'
    }`}>
      {/* Main background with gradient */}
      <div className="absolute inset-0 overflow-hidden">
        {isDarkMode ? (
          // Dark mode background - kept mostly the same
          <>
            <div className="absolute -top-1/4 -left-1/4 w-[800px] h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
            <div className="absolute -bottom-1/4 -right-1/4 w-[800px] h-[800px] rounded-full blur-3xl bg-emerald-500/[0.03]" />
          </>
        ) : (
          // Light mode background - updated to be more subtle
          <>
            {/* Main gradient background */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-100 via-emerald-100/20 to-gray-100" />
            
            {/* Additional decorative elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-100/90 via-transparent to-gray-100/90" />
            <div className="absolute inset-0 backdrop-blur-[50px]" />
            
            {/* Subtle pattern for texture */}
            <div className="absolute inset-0 opacity-[0.03]" 
                 style={{
                   backgroundImage: 'url("/images/bgpattern.png")', 
                   backgroundSize: '400px',
                   filter: 'invert(1)', 
                   mixBlendMode: 'multiply'
                 }}>
            </div>
          </>
        )}
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40">
        <div style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }} className="px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className={`text-2xl font-semibold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Deen Helpers</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="relative z-10 px-4 py-6">
        <div className="grid grid-cols-2 gap-4">
          {helpers.map((helper, index) => {
            const Icon = helper.icon;
            return (
              helper.isComingSoon ? (
                <div
                  key={index}
                  className={`group relative overflow-hidden rounded-2xl p-4 transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-white/5 backdrop-blur-xl' 
                      : 'bg-white/80 backdrop-blur-md shadow-sm border border-white/50'
                  }`}
                >
                  <div className="relative z-10">
                    <div className={`w-12 h-12 mb-4 rounded-xl flex items-center justify-center ${
                      isDarkMode
                        ? 'bg-emerald-500/10'
                        : 'bg-emerald-50/80'
                    }`}>
                      <Icon size={24} weight="thin" className={`w-6 h-6 ${
                        isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                      }`} />
                    </div>
                    <h3 className={`text-base font-medium mb-1 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {helper.title}
                    </h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-white/60' : 'text-gray-600'
                    }`}>
                      {helper.description}
                    </p>
                  </div>
                </div>
              ) : (
                <Link
                  key={index}
                  to={helper.link}
                  className={`group relative overflow-hidden rounded-2xl p-4 transition-all duration-300 ${
                    isDarkMode 
                      ? 'bg-white/5 hover:bg-white/10 backdrop-blur-xl' 
                      : 'bg-white/80 hover:bg-white/90 backdrop-blur-md shadow-sm border border-white/50'
                  }`}
                >
                  <div className="relative z-10">
                    <div className={`w-12 h-12 mb-4 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                      isDarkMode
                        ? 'bg-emerald-500/10'
                        : 'bg-emerald-50/80'
                    }`}>
                      <Icon size={24} weight="thin" className={`w-6 h-6 ${
                        isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                      }`} />
                    </div>
                    <h3 className={`text-base font-medium mb-1 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {helper.title}
                    </h3>
                    <p className={`text-sm ${
                      isDarkMode ? 'text-white/60' : 'text-gray-600'
                    }`}>
                      {helper.description}
                    </p>
                  </div>
                </Link>
              )
            );
          })}
        </div>
      </div>
    </div>
  );

  // Desktop version of the page - simplified with only the app download prompt
  const DesktopView = () => (
    <div className={`min-h-screen flex flex-col ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
    }`}>
      {/* Main content area - taking most of the space but not centered */}
      <div className="flex-1 flex flex-col items-center pt-16 pb-8 px-8">
        <div className="w-full max-w-4xl">
          <header className="mb-12 text-center">
            <h1 className={`text-3xl font-semibold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>Deen Helpers</h1>
          </header>
          <DesktopAppPrompt />
        </div>
      </div>
      
      {/* Empty space to push footer to bottom if content is small */}
      <div className="flex-grow"></div>
      
      {/* Footer area at the bottom */}
      <div className="h-16"></div>
    </div>
  );

  // Render the appropriate view based on device type
  return isDesktop ? <DesktopView /> : <MobileView />;
};

export default DeenHelpers; 